import React, { useState } from 'react'
import { ConditionalWrapper } from '../../ConditionalWrapper'
import FbButton, { IFbButtonProps } from '../../FbUI/FbButton'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogContentSize, // Keep this import
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ModalPrimitive'
import clsx from 'clsx'
import { Z_INDEX } from '../../../styles/z-index'

export interface IModalProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.JSX.Element
  title?: string | React.JSX.Element
  description?: string | React.JSX.Element
  customHeader?: React.ReactNode
  size?: DialogContentSize // Use the imported type
  loading?: boolean
  noPadding?: boolean
  noScroll?: boolean

  blockAccept?: boolean
  onAccept?: () => void | Promise<void>
  acceptButtonProps?: IFbButtonProps
  acceptButtonText?: string

  onAlt?: () => void | Promise<void>
  altButtonProps?: IFbButtonProps
  altButtonText?: string
  blockAlt?: boolean

  onCancel?: () => void
  cancelButtonText?: string
  cancelButtonProps?: IFbButtonProps
  blockCancel?: boolean
  disableCloseIcon?: boolean

  preventCloseOnCancel?: boolean
  hideCancelButton?: boolean

  children?: React.ReactNode
  footerChildren?: React.ReactNode

  childrenContainerClassName?: string
  dialogFooterClassName?: string
  smallButton?: boolean
  headerExtra?: React.ReactNode
  scopeBackdrop?: boolean
  blurLevel?: number
}

// Extracted common content rendering logic
const ModalContentInner = (
  props: IModalProps & {
    isLoading: boolean
    altLoading: boolean
    handleAccept: () => void
    handleAltAccept: () => void
  }
) => {
  const { isLoading, altLoading, handleAccept, handleAltAccept, smallButton } =
    props

  const defaultChildrenContainerClassName =
    'flex-1 min-h-0 overflow-x-hidden max-w-full w-full mt-3'
  const defaultDialogFooterClassName =
    'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 py-2' +
    (smallButton ? ' pb-0' : '')
  return (
    <form
      className={'flex flex-col flex-1 min-h-0 overflow-hidden'}
      onSubmit={(e) => {
        e.preventDefault()
        void handleAccept()
      }}
    >
      {props.customHeader ? (
        props.customHeader
      ) : (
        <DialogHeader noPadding={props.noPadding}>
          <div className="flex flex-row justify-between mr-2">
            <div>
              <DialogTitle>{props.title}</DialogTitle>
              <DialogDescription>{props.description}</DialogDescription>
            </div>
            {props.headerExtra}
          </div>
        </DialogHeader>
      )}
      <div
        className={
          props.childrenContainerClassName ||
          clsx(
            defaultChildrenContainerClassName,
            props.noScroll ? 'overflow-y-hidden' : 'overflow-y-auto'
          )
        }
      >
        {props.children}
      </div>
      <div className={'mb-2 flex-shrink-0'}>{props.footerChildren}</div>

      <DialogFooter
        className={props.dialogFooterClassName || defaultDialogFooterClassName}
        noPadding={props.noPadding}
      >
        {/* CANCEL BUTTON */}
        {!props.hideCancelButton && (
          <ConditionalWrapper
            condition={!props.preventCloseOnCancel}
            wrapper={(children) => (
              <DialogClose disabled={isLoading} asChild>
                {children}
              </DialogClose>
            )}
          >
            <FbButton
              disabled={isLoading || props.blockCancel || altLoading}
              size={props.smallButton ? 'sm' : 'lg'}
              variant="secondary"
              onClick={() => props?.onCancel?.()}
              className={'flex-1'}
              {...props.cancelButtonProps}
            >
              {props.cancelButtonText ?? 'Cancel'}
              {props.cancelButtonProps?.children}
            </FbButton>
          </ConditionalWrapper>
        )}

        {/* ALT BUTTON */}
        {props.onAlt && (
          <FbButton
            loading={altLoading}
            size={props.smallButton ? 'sm' : 'lg'}
            disabled={props.blockAlt || isLoading}
            variant="secondary"
            type={'button'}
            onClick={handleAltAccept}
            className={'flex-1'}
            {...props.altButtonProps}
          >
            {props.altButtonText}
            {props.altButtonProps?.children}
          </FbButton>
        )}

        {/*ACCEPT BUTTON*/}
        {props.onAccept && (
          <FbButton
            disabled={props.blockAccept || altLoading}
            loading={isLoading}
            size={props.smallButton ? 'sm' : 'lg'}
            className={'flex-1'}
            type="button"
            onClick={handleAccept}
            {...props.acceptButtonProps}
          >
            {props.acceptButtonText}
            {props.acceptButtonProps?.children}
          </FbButton>
        )}
      </DialogFooter>
    </form>
  )
}

export function Modal(props: IModalProps) {
  const { disableCloseIcon, open, onOpenChange, ...rest } = props
  const [isLoading, setLoading] = useState(false)
  const [altLoading, setAltLoading] = useState(false)

  async function handleAccept() {
    setLoading(true)
    try {
      await props.onAccept?.()
    } finally {
      setLoading(false)
    }
  }

  async function handleAltAccept() {
    setAltLoading(true)
    await props.onAlt?.()
    setAltLoading(false)
  }

  // Render scoped version if scopeBackdrop is true
  if (props.scopeBackdrop) {
    if (!props.open) return null

    // Map DialogContentSize to Tailwind max-w classes
    const sizeClasses: Record<DialogContentSize, string> = {
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      '2xl': 'max-w-5xl', // Example, adjust if needed
      '3xl': 'max-w-6xl', // Example, adjust if needed
      full: 'max-w-full',
    }
    const maxWidthClass = props.size ? sizeClasses[props.size] : sizeClasses.md // Default to md

    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: `blur(${props.blurLevel ?? 0}px)`,
          zIndex: Z_INDEX.MODAL_BASE,
        }}
      >
        <Dialog open={open} onOpenChange={onOpenChange}>
          <div
            className={clsx(
              'relative bg-white rounded-lg shadow-lg flex flex-col m-4', // Added margin for spacing
              maxWidthClass, // Use mapped size class
              'w-full', // Ensure it takes width up to max-w
              props.noPadding ? '' : 'p-6' // Apply padding here for the container
            )}
            style={{ maxHeight: '90vh' }} // Limit height
          >
            {/* Close button for scoped modal */}
            {!disableCloseIcon && (
              <button
                type="button"
                className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                onClick={() => props.onOpenChange?.(false)}
                disabled={isLoading || altLoading}
                aria-label="Close"
              >
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            {/* Render the common content */}
            <ModalContentInner
              {...rest}
              isLoading={isLoading}
              altLoading={altLoading}
              handleAccept={handleAccept}
              handleAltAccept={handleAltAccept}
            />
          </div>
        </Dialog>
      </div>
    )
  }

  // Original Dialog rendering for global backdrop
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.trigger ? (
        <DialogTrigger asChild>{props.trigger}</DialogTrigger>
      ) : null}
      <DialogContent
        id={'fb-modal-content'}
        noPadding={props.noPadding}
        size={props.size}
        disableCloseIcon={props.disableCloseIcon}
      >
        {/* Render the common content */}
        <ModalContentInner
          {...props}
          isLoading={isLoading}
          altLoading={altLoading}
          handleAccept={handleAccept}
          handleAltAccept={handleAltAccept}
        />
      </DialogContent>
    </Dialog>
  )
}
